import { DateTimePicker } from '@components/DateTimePicker'
import Modal from '@components/Modal'
import { useAuth } from '@context/AuthContext'
import { useCreateFlightAuto, useCreateFlightManual } from '@hooks'
import { GetFlightGroupResponse, GetUserType, PostFlightManualType } from '@orbit/api-types'
import { isNotFound } from '@lib/apiError'
import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useParams } from 'react-router-dom'
import { inputToWallClock } from '@lib/datetime'
import { getDepartureDisplay, getFlightCode, getRouteString } from '../flightHelpers'
import {
	AddAnotherLegButton,
	Button,
	ButtonGroup,
	ErrorText,
	FieldRow,
	FormDescription,
	FormGroup,
	FormInput,
	FormLabel,
	LegSummaryCode,
	LegSummaryRoute,
	LegSummaryRow,
	LegSummaryText,
	LegSummaryTime,
	LegsSummary,
	LegsSummaryTitle,
	ManualFallbackNotice,
	PassengerCheckbox,
	PassengerName,
	PassengerRowStyled,
	PassengersList,
	ProfileAvatar,
	SummaryBox,
	Tab,
	TabRow,
} from './AddFlightModal.styles'

type Mode = 'auto' | 'manual'

interface AddFlightModalProps {
	isOpen: boolean
	onClose: () => void
	members: GetUserType[]
	initialFlightGroup?: GetFlightGroupResponse
}

const getInitials = (member: GetUserType) => {
	const first = member.firstName?.[0] ?? ''
	const last = member.lastName?.[0] ?? ''
	return (first + last).toUpperCase() || '?'
}

const AddFlightModal: React.FC<AddFlightModalProps> = ({ isOpen, onClose, members, initialFlightGroup }) => {
	const { tripId } = useParams<{ tripId: string }>()
	const { user } = useAuth()
	const createAuto = useCreateFlightAuto()
	const createManual = useCreateFlightManual()

	const [mode, setMode] = useState<Mode>('auto')
	const [flightGroup, setFlightGroup] = useState<GetFlightGroupResponse | null>(null)
	const [manualFallbackActive, setManualFallbackActive] = useState(false)

	const [flightCode, setFlightCode] = useState('')
	const [flightDate, setFlightDate] = useState('')

	const [manualAirlineIata, setManualAirlineIata] = useState('')
	const [manualFlightNumber, setManualFlightNumber] = useState('')
	const [manualDepartureAirport, setManualDepartureAirport] = useState('')
	const [manualArrivalAirport, setManualArrivalAirport] = useState('')
	const [manualDepartureTime, setManualDepartureTime] = useState('')
	const [manualArrivalTime, setManualArrivalTime] = useState('')

	const [selectedPassengers, setSelectedPassengers] = useState<Set<number>>(new Set())

	useEffect(() => {
		if (isOpen) {
			setMode('auto')
			setFlightGroup(initialFlightGroup ?? null)
			setManualFallbackActive(false)
			setFlightCode('')
			setFlightDate('')
			setManualAirlineIata('')
			setManualFlightNumber('')
			setManualDepartureAirport('')
			setManualArrivalAirport('')
			setManualDepartureTime('')
			setManualArrivalTime('')
			const presetPassengerIds = initialFlightGroup
				? Array.from(new Set(initialFlightGroup.flights.flatMap(l => l.passengers.map(p => p.userId))))
				: members.map(m => m.id)
			setSelectedPassengers(new Set(presetPassengerIds))
		}
	}, [isOpen, members, initialFlightGroup])

	const sortedMembers = useMemo(() => {
		return [...members].sort((a, b) => {
			if (a.id === user?.id) return -1
			if (b.id === user?.id) return 1
			return 0
		})
	}, [members, user?.id])

	const togglePassenger = (userId: number) => {
		setSelectedPassengers(prev => {
			const next = new Set(prev)
			if (next.has(userId)) {
				next.delete(userId)
			} else {
				next.add(userId)
			}
			return next
		})
	}

	const getMemberName = (member: GetUserType) => {
		const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ')
		return member.id === user?.id ? `${fullName} (You)` : fullName
	}

	const isAutoValid = flightCode.trim().length > 0 && flightDate.length > 0 && selectedPassengers.size > 0
	const isManualValid =
		manualAirlineIata.trim().length > 0 &&
		manualDepartureTime.length > 0 &&
		selectedPassengers.size > 0

	const isSubmitting = createAuto.isPending || createManual.isPending

	const handleAutoSubmit = async () => {
		if (!tripId || !isAutoValid) return
		try {
			const group = await createAuto.mutateAsync({
				tripId,
				data: {
					flightCode: flightCode.trim(),
					date: flightDate,
					passengerUserIds: Array.from(selectedPassengers),
					flightGroupId: flightGroup?.id,
				},
			})
			setFlightGroup(group)
			setFlightCode('')
			setFlightDate('')
			setManualFallbackActive(false)
			toast.success('Flight leg added!')
		} catch (error) {
			if (isNotFound(error)) {
				const { airlineIata, flightNumber } = splitCode(flightCode)
				setManualAirlineIata(airlineIata)
				setManualFlightNumber(flightNumber)
				if (flightDate) {
					setManualDepartureTime(`${flightDate}T12:00`)
				}
				setMode('manual')
				setManualFallbackActive(true)
				toast(
					'Flight not found in lookup. Enter the details manually.',
					{ icon: '✈︎' }
				)
			} else {
				toast.error('Unable to add flight leg. Please try again.')
			}
		}
	}

	const handleManualSubmit = async () => {
		if (!tripId || !isManualValid) return
		const legParam: PostFlightManualType['flights'][number] = {
			airlineIata: manualAirlineIata.trim().toUpperCase(),
			flightNumber: manualFlightNumber.trim() || undefined,
			departureAirportCode: manualDepartureAirport.trim().toUpperCase() || undefined,
			arrivalAirportCode: manualArrivalAirport.trim().toUpperCase() || undefined,
			departureTimeStr: inputToWallClock(manualDepartureTime),
			arrivalTimeStr: manualArrivalTime ? inputToWallClock(manualArrivalTime) : undefined,
		}
		try {
			const group = await createManual.mutateAsync({
				tripId,
				data: {
					flights: [legParam],
					passengerUserIds: Array.from(selectedPassengers),
					flightGroupId: flightGroup?.id,
				},
			})
			setFlightGroup(group)
			setManualAirlineIata('')
			setManualFlightNumber('')
			setManualDepartureAirport('')
			setManualArrivalAirport('')
			setManualDepartureTime('')
			setManualArrivalTime('')
			setManualFallbackActive(false)
			toast.success('Flight leg added!')
		} catch {
			toast.error('Unable to add flight leg. Please try again.')
		}
	}

	const handleAddAnotherLeg = () => {
		setMode('auto')
		setManualFallbackActive(false)
	}

	const handleDone = () => {
		onClose()
	}

	const title = flightGroup
		? `Flight (${flightGroup.flights.length} ${flightGroup.flights.length === 1 ? 'leg' : 'legs'})`
		: 'Add Flight'

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={title}
			size="large"
			backgroundColor="#fff8f6"
			closeButtonColor="#a03c04"
			closeOnBackdropClick={false}
			footer={
				<ButtonGroup>
					{flightGroup ? (
						<>
							<Button type="button" variant="secondary" onClick={handleDone}>
								Done
							</Button>
							<Button
								type="button"
								variant="primary"
								disabled={!canSubmitActiveForm(mode, isAutoValid, isManualValid, isSubmitting)}
								onClick={mode === 'auto' ? handleAutoSubmit : handleManualSubmit}
							>
								{isSubmitting ? 'Adding…' : 'Add Leg'}
							</Button>
						</>
					) : (
						<>
							<Button type="button" variant="secondary" onClick={onClose}>
								Cancel
							</Button>
							<Button
								type="button"
								variant="primary"
								disabled={!canSubmitActiveForm(mode, isAutoValid, isManualValid, isSubmitting)}
								onClick={mode === 'auto' ? handleAutoSubmit : handleManualSubmit}
							>
								{isSubmitting ? 'Adding…' : 'Add Flight'}
							</Button>
						</>
					)}
				</ButtonGroup>
			}
		>
			{flightGroup && (
				<LegsSummary>
					<LegsSummaryTitle>Legs added</LegsSummaryTitle>
					{flightGroup.flights.map(leg => {
						const depStr = getDepartureDisplay(leg)
						return (
							<LegSummaryRow key={leg.id}>
								<LegSummaryText>
									<LegSummaryCode>{getFlightCode(leg)}</LegSummaryCode>
									<LegSummaryRoute>{getRouteString(leg)}</LegSummaryRoute>
									{depStr && <LegSummaryTime>Departs {depStr}</LegSummaryTime>}
								</LegSummaryText>
							</LegSummaryRow>
						)
					})}
					<AddAnotherLegButton type="button" onClick={handleAddAnotherLeg}>
						+ Add Another Leg
					</AddAnotherLegButton>
				</LegsSummary>
			)}

			<TabRow>
				<Tab $active={mode === 'auto'} onClick={() => setMode('auto')}>Auto Lookup</Tab>
				<Tab $active={mode === 'manual'} onClick={() => setMode('manual')}>Manual Entry</Tab>
			</TabRow>

			{manualFallbackActive && mode === 'manual' && (
				<ManualFallbackNotice>
					We couldn't automatically pull this flight. Enter the details manually below.
				</ManualFallbackNotice>
			)}

			{mode === 'auto' ? (
				<>
					<FormGroup>
						<FormLabel htmlFor="flight-code">Flight Code</FormLabel>
						<FormDescription>e.g. AC123, UA2521</FormDescription>
						<FormInput
							id="flight-code"
							type="text"
							value={flightCode}
							onChange={e => setFlightCode(e.target.value.toUpperCase())}
							placeholder="AC123"
							maxLength={10}
						/>
					</FormGroup>

					<FormGroup>
						<FormLabel htmlFor="flight-date">Departure Date</FormLabel>
						<DateTimePicker
							id="flight-date"
							value={flightDate}
							onChange={setFlightDate}
							showTime={false}
							placeholder="Select departure date"
						/>
					</FormGroup>
				</>
			) : (
				<>
					<FieldRow>
						<FormGroup>
							<FormLabel htmlFor="manual-airline">Airline IATA Code</FormLabel>
							<FormInput
								id="manual-airline"
								type="text"
								value={manualAirlineIata}
								onChange={e => setManualAirlineIata(e.target.value.toUpperCase())}
								placeholder="AC"
								maxLength={3}
							/>
						</FormGroup>
						<FormGroup>
							<FormLabel htmlFor="manual-number">Flight Number</FormLabel>
							<FormInput
								id="manual-number"
								type="text"
								value={manualFlightNumber}
								onChange={e => setManualFlightNumber(e.target.value)}
								placeholder="123"
								maxLength={6}
							/>
						</FormGroup>
					</FieldRow>

					<FieldRow>
						<FormGroup>
							<FormLabel htmlFor="manual-dep-airport">Departure Airport</FormLabel>
							<FormInput
								id="manual-dep-airport"
								type="text"
								value={manualDepartureAirport}
								onChange={e => setManualDepartureAirport(e.target.value.toUpperCase())}
								placeholder="YYZ"
								maxLength={3}
							/>
						</FormGroup>
						<FormGroup>
							<FormLabel htmlFor="manual-arr-airport">Arrival Airport</FormLabel>
							<FormInput
								id="manual-arr-airport"
								type="text"
								value={manualArrivalAirport}
								onChange={e => setManualArrivalAirport(e.target.value.toUpperCase())}
								placeholder="JFK"
								maxLength={3}
							/>
						</FormGroup>
					</FieldRow>

					<FieldRow>
						<FormGroup>
							<FormLabel htmlFor="manual-dep-time">Departure Time</FormLabel>
							<DateTimePicker
								id="manual-dep-time"
								value={manualDepartureTime}
								onChange={next => {
									setManualDepartureTime(next)
									if (manualArrivalTime && next && next > manualArrivalTime) {
										setManualArrivalTime('')
									}
								}}
								placeholder="Select departure"
							/>
						</FormGroup>
						<FormGroup>
							<FormLabel htmlFor="manual-arr-time">Arrival Time (Optional)</FormLabel>
							<DateTimePicker
								id="manual-arr-time"
								value={manualArrivalTime}
								onChange={next => {
									setManualArrivalTime(next)
									if (manualDepartureTime && next && next < manualDepartureTime) {
										setManualDepartureTime('')
									}
								}}
								placeholder="Select arrival"
							/>
						</FormGroup>
					</FieldRow>
				</>
			)}

			<FormGroup>
				<FormLabel>Passengers</FormLabel>
				<FormDescription>Select who is on this flight leg</FormDescription>
				<PassengersList>
					{sortedMembers.map(member => {
						const isSelected = selectedPassengers.has(member.id)
						return (
							<PassengerRowStyled
								key={member.id}
								$isSelected={isSelected}
								onClick={() => togglePassenger(member.id)}
							>
								<PassengerCheckbox $checked={isSelected} />
								<ProfileAvatar>
									{member.profilePicture ? (
										<img src={member.profilePicture} alt={getMemberName(member)} />
									) : (
										getInitials(member)
									)}
								</ProfileAvatar>
								<PassengerName>{getMemberName(member)}</PassengerName>
							</PassengerRowStyled>
						)
					})}
				</PassengersList>
				<SummaryBox>
					{selectedPassengers.size === 0 ? (
						<ErrorText>Select at least one passenger</ErrorText>
					) : (
						`${selectedPassengers.size} ${selectedPassengers.size === 1 ? 'passenger' : 'passengers'} selected`
					)}
				</SummaryBox>
			</FormGroup>
		</Modal>
	)
}

const splitCode = (code: string): { airlineIata: string; flightNumber: string } => {
	const trimmed = code.trim().replace(/\s/g, '').toUpperCase()
	const digitIndex = trimmed.search(/\d/)
	if (digitIndex === -1) return { airlineIata: trimmed, flightNumber: '' }
	return {
		airlineIata: trimmed.slice(0, digitIndex),
		flightNumber: trimmed.slice(digitIndex),
	}
}

const canSubmitActiveForm = (mode: Mode, autoValid: boolean, manualValid: boolean, submitting: boolean): boolean => {
	if (submitting) return false
	return mode === 'auto' ? autoValid : manualValid
}

export default AddFlightModal
